import { Component, OnInit, OnDestroy } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { Subject, Subscription } from 'rxjs';
import { AXErrorMessageDisplay, AXErrorMessages, ValidationResult } from '../../../../models/formly.model';
import { AxService } from '../../../../services/application/ax.service';
import { VariableService } from '../../../../services/utilities/variables.service';
import {
    ButtonType,
    Colors,
    ConfirmationResponse,
    FormlyType,
    Icons,
    Schemas,
    Size,
    StorageObjects,
    UserType
} from '../../../../utilities/enum';
import { ConfirmModalComponent } from '../../../../modals/confirm-modal/confirm-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { StorageService } from '@yellowtail-frontend/storage-service';
import { CdmService } from '../../../../services/application/cdm.service';
import { tenant } from 'src/tenants/tenant';
import moment from 'moment';
import { HelperService } from '../../../../services/utilities/helper.service';
import { ConfirmationOptions } from '../../../../models/modal.model';
import { TenantService } from '../../../../services/application/tenant.service';
import { FormArray, FormControl, FormGroup, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { ThisReceiver } from '@angular/compiler';

@Component({
    selector: 'formly-object-type',
    templateUrl: './object.component.html',
    styleUrls: ['./object.component.scss'],
    standalone: false
})
export class ObjectComponent extends FieldType implements OnInit, OnDestroy {
    tenant = tenant;
    expandable: boolean;
    open: boolean;
    icons = Icons;
    size = Size;
    colors = Colors;
    buttonType = ButtonType;
    schemas = Schemas;
    index = -1;
    isAllowedToRemoveFromArray: boolean;
    formError: boolean = false;
    errors: AXErrorMessages = { count: 0, errors: [], type: null };
    errorList: AXErrorMessageDisplay = {};
    axErrorObject: AXErrorMessages = { count: 0, errors: [], type: null };
    axErrorDisplayObject: AXErrorMessageDisplay = {};
    displayText: string = '';
    displayHeader: boolean = true;
    confirmation: ConfirmationOptions = {
        title: 'Let op!',
        subtitle: 'Weet je zeker dat je dit onderdeel wil verwijderen?',
        confirmButton: 'Verwijder',
        rejectButton: 'Annuleer'
    };

    onDestroy$: Subject<boolean> = new Subject();

    private subscription = new Subscription();
    private readonly deleteSubscription = new Subscription();
    private subscriptionHiddenFields = new Subscription();

    constructor(
        public dialog: MatDialog,
        private readonly _axService: AxService,
        private readonly _cdmService: CdmService,
        private readonly _variableService: VariableService,
        private readonly _storageService: StorageService,
        private readonly _helperService: HelperService,
        private readonly _tenantService: TenantService
    ) {
        super();
        this.axErrorDisplayObject.errors = [];
    }

    calcFlexValue(field) {
        const styles: {flex?: string, maxWidth?: string, display?: string} = {};

        if (field.isHidden) {
            styles.display = 'none';
        } else {
            const gap = 20;
            const width = field.styles?.columnWidth > 100 ? 100 : field.styles?.columnWidth ?? 100;

            styles.flex = `0 0 calc(${width}% - ${gap}px)`;
            styles.maxWidth = width + '%';
        }

        return styles;
    }

    findMissingKeyNumberValue(arrayOfNumbers, n) {
        let totalSum = (n * (n + 1)) / 2;
        let sumOfArray = 0;

        for (let i of arrayOfNumbers) {
            sumOfArray += i;
        }

        return totalSum - sumOfArray;
    }

    ngOnInit(): void {

        if (this.isParentObjectArrayType()) {
            this.field['showRemoveButton'] = true;
        }

        if (this.field.parent.type === 'array' && this.field.parent.fieldGroup.length >= this.field.parent.templateOptions.maxItems) {
            this.field.parent['showAddButton'] = false;
        }

        this.formError = this.form.controls[this.to.label]?.status === 'INVALID';
        this.index = this.field['index'];

        // HIDE ALL BUTTONS FOR ADVISORS ON THE GEGEVENS PAGE
        // Agents and SuperAgents are not able to add or delete items from a Dossier application.
        const _userType = this._storageService.get<string>(StorageObjects.userType);
        if ((_userType === UserType[UserType.Agent] || _userType === UserType[UserType.SuperAgent]) && this._variableService.formlyParent === 'details') {
            this.field['showRemoveButton'] = false;
        }

        if (this._variableService.formlyParent === 'overview' && this.field.parent.key === 'PartijNAWData') {
            // We no longer allow the user to manually add a PERSON object, they must
            // add a corresponding object to add the PERSON, for example a NOTARIS object.
            this.field['showRemoveButton'] = false;
        }

        this.isAllowedToRemoveFromArray = this.field?.parent?.type?.toString().toLowerCase() === 'array';
        if (this.to.description && this.to.description.toLowerCase() !== 'oneof') {
            this.expandable = JSON.parse(this.to.description)?.expandable;
        }

        this.validateOneOfValidationType();

        if (this.to.label === 'OfferteAanvraagType') {
            this.displayHeader = false;
        }

        if (this.field.key === 'Header') { //  && (this.model?.v2 || !this.model)
            const HEADER_FORM_GROUP: FormGroup = this.field.parent.form.get('Header') as FormGroup;
            Object.keys(HEADER_FORM_GROUP.controls).forEach(key => {
                const control = HEADER_FORM_GROUP.get(key);
            
                if (key !== 'OntvangerCode') { // OntvangerNrHDN
                  if (control instanceof FormGroup) {
                    // If it's a nested FormGroup, disable all controls within it recursively
                    this.disableAllInFormGroup(control);
                  } else {
                    control?.disable();  // Disable other controls
                  }
                }
              });
        }

        const KEYS = [];
        let newVolgNumber = 0;
        let newIdentifierNumber = 0;

        if (this.field.parent.fieldGroup.length > 1) {
            newIdentifierNumber = this.field.parent.fieldGroup[this.field.parent.fieldGroup.length - 2].model?.['Volgnummer'];

            if (!newIdentifierNumber) {
                const PREVIOUS_RECORD_VOLGNUMBER = this.field.parent.fieldGroup[parseInt(this.field.key.toString()) - 1]?.model?.Volgnummer ?? 1
                newIdentifierNumber = PREVIOUS_RECORD_VOLGNUMBER;
            }

            newVolgNumber = parseInt(newIdentifierNumber ? newIdentifierNumber.toString() : '0') + 1;           
        } else {
            newIdentifierNumber = 0;
            newVolgNumber = 1;
        }

        if (this.field.model) {
            if (this.to.label && this.to.label.toLowerCase() === 'item') {
            
                let key_actual_length = 0;
                this.field.parent.fieldGroup.forEach(values => {
                    const VALUE_TO_ADD = parseInt(values.key.toString());
                    if (!KEYS.includes(VALUE_TO_ADD)) {
                        KEYS.push(VALUE_TO_ADD);
                    }
                    key_actual_length++;
                })
                
                const MISSING_KEY_VALUE = (this.findMissingKeyNumberValue(KEYS, key_actual_length)) - 1;
    
                if (this.field.model?.['special']) {
                    const { special: LINKED_TYPE, ID: LINKED_TYPE_ID } = this.field.model;
                    const LINKED_TYPE_PARENT = this.field.parent.model.find(link => link.ID === LINKED_TYPE_ID);
    
                    const LINKED_TYPE_PARENT_EXISTING_SORTED = this.field.parent.model
                        .filter(parent => parent.special === LINKED_TYPE)
                        .sort((a, b) => a.specialIndex - b.specialIndex);
    
                    const LAST_LINKED_TYPE = LINKED_TYPE_PARENT_EXISTING_SORTED[LINKED_TYPE_PARENT_EXISTING_SORTED.length - 2];
                    
                    const SPECIAL_INDEX_NEW_NUMBER = LAST_LINKED_TYPE?.specialIndex + 1 || 0;
                    const DEFAULT_NUMBER = this.field.parent.parent.model[LINKED_TYPE].length
    
                    const NEW_LINKED_INDEX = LAST_LINKED_TYPE?.specialIndex ? SPECIAL_INDEX_NEW_NUMBER : DEFAULT_NUMBER;
    
                    LINKED_TYPE_PARENT['specialIndex'] = NEW_LINKED_INDEX;
                    newIdentifierNumber = NEW_LINKED_INDEX;
                } else if (this.field.parent.fieldGroup.length > 1) {
                    newIdentifierNumber = this.field.model?.['Volgnummer'] ?? newVolgNumber;
                } else {
                    newIdentifierNumber = 1;
                }
    
                if (this.field['above'] === 'Personen' && (this.field.model.SoortPartij === '01 natuurlijk persoon' || !this.field.model.SoortPartij)) {
                    this.field.parent.parent.model.Hypotheekgever.forEach((mortgage, index) => {
                       if (mortgage.RefPartijNAWData.IDREF.toString() === this.field.model.ID.toString()) {
                            this.displayText = 'Hypotheekgever ' + (index + 1).toString();
                       }
                    })
    
                    this.field.parent.parent.model.Notaris.forEach((notes, index) => {
                        if (notes.RefContactPersoonData.IDREF.toString() === this.field.model.ID.toString()) {
                            this.displayText = 'Notaris ' + (index + 1).toString();
                        }
                    })
    
                    //RefContactPersoonData
                } else if (this.field['above'] === 'Personen' && !this.field.model.SoortPartij) {
                    this.displayText = 'Nieuwe ' + (parseInt(newIdentifierNumber.toString(), 0) + 0).toString();
                } else {
                    this.displayText = (this.field['arrayText'] ? this.field['arrayText']
                        : this.field['above'] ? this.field['above'] === 'Personen' ? 'Tussenpersoon'
                        : this.field['above']
                        : this.field.model['special'] ? this.field.model['special']
                        : 'Nieuwe')
                        + ' ' + (parseInt(newIdentifierNumber.toString(), 0) + 0).toString();
                }
    
                
            } else if (this.to?.label === '' && (!isNaN(Number(this.field.key)))) {
                let _displayNewNumber = this.field.model?.Volgnummer ? this.field.model.Volgnummer : newVolgNumber;
    
                this.displayText = (this.field['arrayText'] ? this.field['arrayText'] : this.field['above'] ? this.field['above'] : 'Nieuwe') + ' ' + _displayNewNumber;
            } else if (this.to.label && this.to.label !== '') {
                const _titleReplacement = this._variableService.axDisplayTextLookUpData.find(x => x.key.toLowerCase() === this.to.label.toLowerCase());
                if (_titleReplacement) {
                    this.displayText = _titleReplacement.value;
                } else {
                    this.displayText = this.to.label;
                }
            } else {
                this.setDisplayTextUsingFormlyFieldKey(this.field.key);
            }
        } else {
            this.setDisplayTextUsingFormlyFieldKey(this.field.key);
        }
        

        

        // ADD VOLGNUMMER IF EMPTY
        this.addNumber(this.field, newVolgNumber);

        // SUBSCRIPTIONS
        this.subscription.add(this._axService.alerts$.subscribe((errors: ValidationResult[]) => {
            let _childErrorObject = false;
            this.axErrorObject = { count: 0, errors: [], type: null };
            this.axErrorDisplayObject.errors = [];
            if (errors && errors.length > 0) {

                let MATCHING_REF_ERROR;
                for (const error of errors) {
                    if (
                        (this.model && error?.Id?.toLowerCase() === this.model['Cord_Ref']?.toLowerCase()) ||
                        (this.model && error['childRef'] && error['childRef']?.toLowerCase() === this.model['Cord_Ref']?.toLowerCase())
                    ) {
                        MATCHING_REF_ERROR = error;

                        if (this.model.hasOwnProperty(error.Property)) {
                            const value = this.model[error.Property];
                            if (value instanceof Object) {
                                error['childRef'] = value.Cord_Ref;
                            }
                        } else {
                            if (MATCHING_REF_ERROR.Message.toLowerCase() !== 'manualentry') {
                                this.axErrorDisplayObject.errors.push(MATCHING_REF_ERROR.Message);
                                this.axErrorDisplayObject.type = MATCHING_REF_ERROR.Type.toLowerCase() || 'error';
                            }
                        }
                    }
                }

                if (MATCHING_REF_ERROR) {
                    this.axErrorObject.count = 1;
                    this.axErrorObject.type = MATCHING_REF_ERROR.Type.toLowerCase();
                }

                // Matching error
                if (this.axErrorObject.count > 0) {

                    if (this.model === undefined || _childErrorObject) {
                        this.axErrorDisplayObject.type = MATCHING_REF_ERROR.Type.toLowerCase() || 'error';
                        if (MATCHING_REF_ERROR.Message.toLowerCase() !== 'manualentry') {
                            this.axErrorDisplayObject.errors.push(MATCHING_REF_ERROR.Message);
                        }
                    }
                }

                // Add UI messages
                this._helperService.addFormlyUIErrorMessages(this.axErrorObject, this.to?.label?.toString(), this.model?.Cord_Ref);
            }

            this.validateOneOfValidationType();
        }));

        const BUY_TO_LET_SUBSCRIPTION = !this._cdmService.buyToLetDeleteAllFields.closed ? this._cdmService.buyToLetDeleteAllFields.subscribe(data => {
            if (data) {
                this.removeItem(this._variableService.buyToLetModel, true);
                this._cdmService.buyToLetDeleteAllFields.next(undefined);
            }
        }) : undefined;

        if (BUY_TO_LET_SUBSCRIPTION) {
            this.deleteSubscription.add(BUY_TO_LET_SUBSCRIPTION);
        }

        const HIDDEN_FIELDS_SUBSCRIPTION = !this._cdmService.hiddenFields.closed ? this._cdmService.hiddenFields.subscribe((fields) => {

            if (this.field?.key?.toString().toLowerCase() === 'arbeidsmarktscan') {
                const FORM = this.form as FormGroup;
                if (FORM) {
                    const EMPLOYMENT_ARRAY = FORM.controls.DienstBetrekking as FormArray;
                    if (EMPLOYMENT_ARRAY?.length > 0) {
                        this.field.hide = true;
                        for (let i = 0; i < EMPLOYMENT_ARRAY.controls.length; i++) {
                            const EMPLOYMENT: UntypedFormGroup = EMPLOYMENT_ARRAY.controls[i] as UntypedFormGroup;
                            if (EMPLOYMENT.controls.ArbeidsmarktscanJN?.value === 'J Ja') {
                                this.field.hide = false;
                                break;
                            }
                        }
                    } else {
                        this.field.hide = true;
                    }
                }
            } else if (this.field?.key?.toString().toLowerCase() === 'overbrugging') {
                const FORM = this.form as UntypedFormGroup;
                if (FORM) {
                    const OVERBRUGGING_SELECTION = FORM.controls.OverbruggingJN as FormControl;
                    if (OVERBRUGGING_SELECTION) {
                        if (OVERBRUGGING_SELECTION.value === 'J Ja') {
                            this.field.hide = false;
                        } else {
                            this.field.hide = true;
                        }
                    }
                }
            } else {
                const RESULT = this._cdmService.hideFields(fields, this.field);
                this.field.hide = RESULT;
            }

        }) : undefined;

        if (HIDDEN_FIELDS_SUBSCRIPTION) {
            this.subscriptionHiddenFields.add(HIDDEN_FIELDS_SUBSCRIPTION);
        }
    }

    disableAllInFormGroup(formGroup: FormGroup) {
        Object.keys(formGroup.controls).forEach(key => {
          const control = formGroup.get(key);
          control?.disable();
        });
      }

    checkIfAllValuesNull(data: any): any {
        let RETURN_VALUE = true;
        const PROPERTIES = Object.keys(data);

        PROPERTIES.forEach(field => {
            const FORM_CONTROL = data[field];

            if (FORM_CONTROL) {
                if (FORM_CONTROL.value && Object.prototype.toString.call(FORM_CONTROL.value) === '[object Array]') {
                    this.checkIfAllValuesNull(FORM_CONTROL.controls);
                } else if (FORM_CONTROL.value && typeof FORM_CONTROL.value === 'object') {
                    this.checkIfAllValuesNull(FORM_CONTROL.controls);
                } else if (FORM_CONTROL.value) {
                    RETURN_VALUE = false;
                }
            }
        });

        return RETURN_VALUE;
    }

    removeValidatorsFromNullObjects(data: FormGroup): void {
        Object.keys(data.controls).forEach(key => {
            const CONTROL_FORMGROUP = data.controls[key] as FormGroup;
            if (CONTROL_FORMGROUP.controls) {
                this.removeValidatorsFromNullObjects(CONTROL_FORMGROUP);
            }

            data.controls[key].clearValidators();
            data.controls[key].updateValueAndValidity();
          });
    }

    setDisplayTextUsingFormlyFieldKey(key: string | number | Array<number | string>): void {
        const _titleReplacement = this._variableService.axDisplayTextLookUpData.find(x => x.key.toLowerCase() === key.toString().toLowerCase());
        if (_titleReplacement) {
            this.displayText = _titleReplacement.value;
        } else {
            this.displayText = key.toString();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.deleteSubscription.unsubscribe();

        this.onDestroy$.next(true);
    }

    // ADD VOLGNUMMER IF EMPTY
    addNumber(currentField, newVolgNumber): void {

        // Check if current field contains volgnummer
        currentField.fieldGroup.forEach((currentFieldGroup) => {
            if (currentFieldGroup.formControl) {
                if (currentFieldGroup?.key?.toString().toLowerCase() === 'volgnummer' && !currentFieldGroup.formControl.value) {
                    if (currentField.model) {
                        currentFieldGroup.model.Volgnummer = newVolgNumber;
                        currentFieldGroup.formControl.markAsTouched();
                    }
                }
            }
        });
    }

    removeFromParent(event): void {
        event.stopPropagation();
        this?.field?.parent?.key.toString();
        this.index = parseInt(this.field['key'].toString());
        if (this.index > -1) {
            const LINKED_OBJECTS = [
                'Hypotheekgever',
                'Notaris'
            ]
            const SUBTITLE = LINKED_OBJECTS.includes(this.field?.parent?.key.toString())
                ? 'Houd er rekening mee dat hierdoor ook het gekoppelde persoonsobject wordt verwijderd'
                : 'Weet je zeker dat je dit onderdeel wil verwijderen?';

            const dialogRef = this.dialog.open(ConfirmModalComponent, {
                data: this._helperService.getConfirmModalData({
                    ...this.confirmation,
                    subtitle: SUBTITLE
                }),
                disableClose: true
            });
            dialogRef.afterClosed().subscribe((result: number) => {
                switch (result) {
                    case ConfirmationResponse.yes:
                        this.field.parent.fieldGroup[this.index]?.form?.markAsDirty();

                        // DAK
                        if (this._tenantService.hasAccessToSchema(this.schemas.HypotheekAanvraag)) {
                            this.removeItem(this._variableService.axData);
                        }

                        if (this.field.parent.fieldGroup.length < this.field.parent.templateOptions.maxItems) {
                            this.field.parent['showAddButton'] = true;
                        }

                        break;
                    case ConfirmationResponse.no:
                        break;
                }
            });
        }
    }

    private removeItem(obj, deleteAll = false): void {
        for (const prop in obj) {
            if (obj.hasOwnProperty(prop)) {

                const VALUE = obj[prop];

                if (typeof VALUE === 'object') {

                    const isArray = VALUE instanceof Array || false;
                    const parentKey = this?.field?.parent?.key?.toString().toLowerCase() || '';
                    const length = VALUE?.length > 0 || false;
                    const valueHasID = VALUE ? VALUE[this.index] && VALUE[this.index]['ID'] : false;

                    if (prop.toLowerCase() === parentKey || deleteAll) {
                        if (!isArray) {
                            return;
                        }

                        // DELETE ALL
                        if (deleteAll && length && valueHasID) {
                            const temp = {
                                key: prop,
                                id: VALUE[this.index]['ID']
                            };
                            const deleteFields = this._cdmService.buyToLetDeleteFields.value || [];
                            deleteFields.push(temp);
                            this._cdmService.buyToLetDeleteFields.next(deleteFields);
                        }

                        const ID_REF_VALUE = VALUE[this.index].RefContactPersoonData?.IDREF || VALUE[this.index].RefPartijNAWData?.IDREF;
                        
                        VALUE.splice(this.index, 1);
                        this.field.parent['showAddButton'] = true;
                        this.field.parent.fieldGroup.splice(this.index, 1);

                        const PERSON_FIELDGROUP_INDEX = this.field.parent.parent.fieldGroup.findIndex(person => person.key === 'PartijNAWData')
                        if (PERSON_FIELDGROUP_INDEX > -1) {
                            const PERSON_GROUP = this.field.parent.parent.fieldGroup[PERSON_FIELDGROUP_INDEX];
                            const PERSON_MODEL_INDEX = PERSON_GROUP.model.findIndex(person => person.ID === ID_REF_VALUE);
                            if (PERSON_MODEL_INDEX >= 0) {
                                PERSON_GROUP.fieldGroup.splice(PERSON_MODEL_INDEX, 1);
                                PERSON_GROUP.model.splice(PERSON_MODEL_INDEX, 1);
                            }
                            const ITEM_ARRAY = this.formControl.parent as FormArray;
                            ITEM_ARRAY.removeAt(this.index);

                            this.field.parent.fieldGroup.forEach((item, index) => {
                                if (parseInt(item.key.toString()) !== index) {
                                    item.key = index.toString();
                                }
                            })
                        }
                    } else {
                        this.removeItem(VALUE);
                    }
                }
            }
        }
        return obj;
    }

    private isParentObjectArrayType(): boolean {
        return this.field.parent.type === 'array';
    }

    private validateOneOfValidationType(): void {
        if (this.to.description && this.to.description.toLowerCase() === 'oneof') {
            let overrideFormControlStatus = true;

            const FORMCONTROLS: FormControl[] = [];
            this.field.fieldGroup.forEach((field: any) => {
                FORMCONTROLS.push(field.formControl);
            })
            overrideFormControlStatus = this.checkIfAllValuesNull(FORMCONTROLS);

            if (overrideFormControlStatus) {
                const FORM_GROUP = this.formControl as FormGroup;

                this.removeValidatorsFromNullObjects(FORM_GROUP);
            }
        }
    }
}
